import { Star } from 'lucide-react';
import { useState } from 'react';
import type { PrintTemplateView } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Badge } from '#client/components/ui/Badge';
import type { Column } from '#client/components/ui/DataTable';
import { ResourceListPage } from '#client/components/ui/ResourceListPage';
import { useMutation } from '#client/hooks/useMutation';
import { RESOURCE } from '#client/lib/routes';
import { useToast } from '#client/providers/ToastProvider';
import { makeDefaultTemplate } from './template.service';
import { dash } from '#client/lib/format';

/**
 * The lab's letterheads.
 *
 * Ordinary CRUD over `ResourceListPage`, plus the one action a template has
 * that a panel or a lab test does not: making it the default. That is a POST to
 * its own sub-path rather than a field on the row, because promoting one
 * template demotes another — a change to a record the operator did not name.
 */
export function TemplateListPage() {
  const toast = useToast();
  const promotion = useMutation('Could not change the default');
  const [promoting, setPromoting] = useState<number>();

  async function handleMakeDefault(template: PrintTemplateView, refresh: () => void) {
    setPromoting(template.id);
    const outcome = await promotion.run(() => makeDefaultTemplate(template.id));
    setPromoting(undefined);
    if (!outcome.ok) {
      toast.error(outcome.message);
      return;
    }
    toast.success(`“${template.name}” is now the default`);
    // Re-read the list: the star has moved off another row, and the table
    // would otherwise show two.
    refresh();
  }

  const columns: Column<PrintTemplateView>[] = [
    { key: 'name', header: 'Template', render: (row) => row.name },
    {
      key: 'default',
      header: 'Default',
      // A Badge, not a StatusBadge: "default" is a property of the row, not a
      // status in the domain's sense, and StatusBadge takes a domain enum.
      render: (row) =>
        row.is_default ? (
          <Badge tone="success" icon={<Star />}>
            Default
          </Badge>
        ) : null,
    },
    {
      key: 'paper',
      header: 'Paper',
      render: (row) => `${row.document.page.size} · ${row.document.page.orientation}`,
    },
    {
      key: 'description',
      header: 'Description',
      render: (row) => dash(row.description),
    },
  ];

  return (
    <ResourceListPage<PrintTemplateView>
      title="Print templates"
      subtitle="What a printed report looks like — paper, letterhead, columns, signatories."
      path={RESOURCE.printTemplates}
      columns={columns}
      noun="template"
      nounPlural="templates"
      searchPlaceholder="Search templates"
      rowLabel={(row) => row.name}
      manageCapability="printTemplate.manage"
      // The default cannot be deleted; the server refuses it too, and hiding
      // the button is kinder than a toast explaining why it did nothing.
      canDeleteRow={(row) => !row.is_default}
      emptyBody="A template describes how a report is printed. The built-in NDC letterhead is created automatically."
      extraRowActions={(row, { refresh }) =>
        row.is_default ? null : (
          <Button
            size="sm"
            icon={<Star />}
            aria-label={`Make ${row.name} the default`}
            loading={promoting === row.id}
            onClick={() => void handleMakeDefault(row, refresh)}
          />
        )
      }
    />
  );
}
